import { inject, Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { HttpParams } from '@angular/common/http';
import {
  GET_LINES_ENDPOINT,
  GET_LT_BOX_CONFIG_ENDPOINT,
  GET_LT_BOX_ENDPOINT,
  UPDATE_LT_BOX_CONFIG_ENDPOINT,
} from '../constants/endpoints.constants';
import { Observable } from 'rxjs';
import {
  GetLinesResponse,
  GetLtBoxParams,
  GetLtBoxResponse,
  GetLvPanelConfigParams,
  GetLvPanelConfigResponse,
  UpdateLvPanelConfigParams,
  UpdateLvPanelConfigRequest,
  UpdateLvPanelConfigResponse,
} from '@data';
import { withSpinner } from '../../app/core/services/spinner.controller';

/**
 * DataRemoteConfigService provides methods for interacting with remote configuration
 * endpoints, specifically for retrieving lv panel identifiers and configurations.
 */
@Injectable({ providedIn: 'root' })
export class DataRemoteConfigService {
  protected readonly apiService = inject(ApiService);

  /**
   * Retrieves the list of lv panel identifiers (lt_box) from the API.
   * @param params - Parameters including centerId and transformerId.
   * @returns An Observable emitting an array of LvPanelIdentifier.
   */
  getLtBox(params: GetLtBoxParams): Observable<GetLtBoxResponse> {
    const endpoint = GET_LT_BOX_ENDPOINT.replace(
      '{{distributorId}}',
      params.distributorId,
    );

    const queryParams = new HttpParams().appendAll({
      centerId: params.centerId,
      transformerId: params.transformerId,
    });

    return this.apiService
      .get<GetLtBoxResponse>(endpoint, queryParams)
      .pipe(withSpinner());
  }

  /**
   * Retrieves the configuration details for a specific lv panel.
   * @param params - Parameters including lvPanelId, centerId and transformerId.
   * @returns An Observable emitting the LvPanelConfiguration.
   */
  getLvPanelConfig(
    params: GetLvPanelConfigParams,
  ): Observable<GetLvPanelConfigResponse> {
    // Replace the placeholders in the endpoint first
    const endpoint = GET_LT_BOX_CONFIG_ENDPOINT.replace(
      '{{distributorId}}',
      params.distributorId,
    ).replace('{{lvPanelId}}', params.lvPanelId);

    // Create query parameters only for centerId and transformerId
    const queryParams = new HttpParams().appendAll({
      centerId: params.centerId,
      transformerId: params.transformerId,
    });

    return this.apiService
      .get<GetLvPanelConfigResponse>(endpoint, queryParams)
      .pipe(withSpinner());
  }

  /**
   * Updates the configuration for a specific lv panel.
   * @param params - Parameters including centerId.
   * @param body - The configuration data to update.
   * @returns An Observable emitting the update response.
   */
  updateLvPanelConfig(
    params: UpdateLvPanelConfigParams,
    body: UpdateLvPanelConfigRequest,
  ): Observable<UpdateLvPanelConfigResponse> {
    // Replace the placeholders in the endpoint
    const endpoint = UPDATE_LT_BOX_CONFIG_ENDPOINT.replace(
      '{{distributorId}}',
      params.distributorId,
    ).replace('{{centerId}}', params.centerId);
    // El body está en camelCase según el modelo
    return this.apiService
      .patch<UpdateLvPanelConfigResponse>(endpoint, body)
      .pipe(withSpinner());
  }

  /**
   * Retrieves the list of lines from the API.
   * @param params - Parameters containing distributorId.
   * @returns An Observable emitting an array of Line objects.
   */
  getLines(params: any): Observable<GetLinesResponse> {
    params = { ...params };
    const queryParams = new HttpParams().appendAll({ ...params });
    return this.apiService
      .get<any>(`${GET_LINES_ENDPOINT}?${queryParams}`)
      .pipe(withSpinner());
  }
}
